const PAGES_URL = "pages"
const TOOLTIPS_URL = "tooltipy"

export const useMainStore = defineStore("main", () => {
    const config = useRuntimeConfig()
    const apiBaseUrl = ref<string>(config.public.backendUrlExt)
    const apiBaseUrlInt = ref<string>(config.public.backendUrlInt)
    const isReady = ref<boolean>(false)
    const mobileMode = ref<boolean>(false)
    const mobileMenuOpen = ref<boolean>(false)
    const mobileLeftPanelOpen = ref<boolean>(false)
    const hasMobileLeftPanel = ref<boolean>(false)
    const headerHeight = ref<number>(0)
    const pages = ref<Array<PageItem | DirectoryPageItem>>([])
    const mapAppPages = ref<string[]>(["atlas", "katalog-lokalit"])
    const tooltips = ref<TooltipyStore>({
        indikatory: [],
        jevy: [],
        limity: [],
        metriky: [],
        skupinyMetrik: [],
        vrstvy: [],
        atlas: [],
    })
    const keywords = ref<KeywordItem[]>([])
    const cookiesBannerActive = ref<boolean>(false)
    const cookiesSettingsModalActive = ref<boolean>(false)
    const cookiesPermissions = ref<Record<CookiesType, boolean>>({
        technical: false,
        analytical: false,
        functional: false,
    })

    const isOnMapAppPage = computed(() => {
        const route = useRoute()
        if (!route.name || typeof route.name !== "string") return false

        return mapAppPages.value.some(page =>
            (route.name as string).split("___")[0]?.includes(page)
        )
    })

    const isOnDirectoryPage = computed(() => {
        const route = useRoute()
        if (!route.name || typeof route.name !== "string") return false
        if (route.path === "/" || route.path === "/en") return true
        return pages.value.some(
            page => (route.name as string).split("___")[0] === page.to.slice(1)
        )
    })

    const getAllPages = computed(() => {
        const allPages: Array<PageItem | DirectoryPageItem> = []
        pages.value.forEach(page => {
            allPages.push(page)
            if (page.children) {
                page.children.forEach(child => {
                    allPages.push(child)
                })
            }
        })
        return allPages
    })

    const fetchPages = async () => {
        try {
            const data = await $fetch<DirectoryPageItem[]>(PAGES_URL, {
                baseURL: import.meta.client
                    ? apiBaseUrl.value
                    : apiBaseUrlInt.value,
            })
            if (data) {
                pages.value = data.sort((a, b) => a.order - b.order)
            }
            return pages.value
        } catch (error) {
            console.warn("Error fetching pages")
            console.warn(error)
        }
    }

    const getPageInfo = (pageName: string) => {
        return getAllPages.value.find(
            page =>
                page.to.slice(1).replaceAll("/", "-") ===
                pageName.split("___")[0]
        )
    }

    const getPageDescription = (pageName: string) => {
        const page = getAllPages.value.find(
            page =>
                page.to.slice(1).replaceAll("/", "-") ===
                pageName.split("___")[0]
        )
        const desc = page ? useLocaleValue(page, "description") : ""
        return desc
    }

    const getChildrenPages = (pageName: string) => {
        const page = pages.value.find(page => page.to === pageName)
        return page ? page.children : []
    }

    const fetchKeywords = async () => {
        try {
            const data = await $fetch<KeywordItem[]>("keywords", {
                baseURL: import.meta.client
                    ? apiBaseUrl.value
                    : apiBaseUrlInt.value,
            })
            keywords.value = data
            return keywords.value
        } catch (error) {
            console.warn("Error fetching keywords")
            console.warn(error)
        }
    }

    const fetchTooltips = async () => {
        try {
            const data = await $fetch<Tooltipy>(TOOLTIPS_URL, {
                baseURL: import.meta.client
                    ? apiBaseUrl.value
                    : apiBaseUrlInt.value,
            })
            tooltips.value = {
                indikatory: data.INDIKATORY,
                jevy: data.JEVY,
                limity: data.LIMITY,
                metriky: data.METRIKY,
                skupinyMetrik: data.SKUPINYMETRIK,
                vrstvy: data.VRSTVY,
                atlas: data.ATLAS,
            }
            return tooltips.value
        } catch (error) {
            console.warn("Error fetching tooltips")
            console.warn(error)
            return null
        }
    }

    const getTooltip = (id: string, type: TooltipType) => {
        const item = tooltips.value[type].find(el => {
            return el.KOD.trim() === id.trim()
        })
        if (type === "metriky") {
            const skupina = tooltips.value.skupinyMetrik.find(
                el =>
                    el.KOD.trim() ===
                    id.trim().split(".").slice(0, -1).join(".")
            )
            if (skupina && item && skupina.NAZEV !== item.NAZEV) {
                return `${useLocaleValue(skupina, "NAZEV")} - ${useLocaleValue(item, "NAZEV").toLocaleLowerCase()}`
            }
            return item ? useLocaleValue(item, "NAZEV") : ""
        }
        return item ? useLocaleValue(item, "NAZEV") : ""
    }

    return {
        apiBaseUrl,
        apiBaseUrlInt,
        isReady,
        mobileMode,
        mobileMenuOpen,
        mobileLeftPanelOpen,
        hasMobileLeftPanel,
        headerHeight,
        pages,
        mapAppPages,
        tooltips,
        keywords,
        cookiesBannerActive,
        cookiesSettingsModalActive,
        cookiesPermissions,
        isOnMapAppPage,
        isOnDirectoryPage,
        getAllPages,
        fetchPages,
        getPageInfo,
        getPageDescription,
        getChildrenPages,
        fetchKeywords,
        fetchTooltips,
        getTooltip,
    }
})

/* const useMainStore = defineStore("main", {
    state: (): AppStore => {
        const config = useRuntimeConfig()
        return {
            apiBaseUrl: config.public.backendUrlExt,
            apiBaseUrlInt: config.public.backendUrlInt,
            isReady: false,
            mobileMode: false,
            mobileMenuOpen: false,
            mobileLeftPanelOpen: false,
            hasMobileLeftPanel: false,
            headerHeight: 0,
            pages: [],
            mapAppPages: ["atlas", "katalog-lokalit"],
            tooltips: {
                indikatory: [],
                jevy: [],
                limity: [],
                metriky: [],
                skupinyMetrik: [],
            },
            keywords: [],
            cookiesBannerActive: false,
            cookiesSettingsModalActive: false,
            cookiesPermissions: {
                technical: false,
                analytical: false,
                functional: false,
            },
        }
    },
    getters: {
        isOnMapAppPage(state) {
            const route = useRoute()
            if (!route.name || typeof route.name !== "string") return false

            return state.mapAppPages.some(page =>
                (route.name as string).split("___")[0]?.includes(page)
            )
        },
        isOnDirectoryPage(state) {
            const route = useRoute()
            if (!route.name || typeof route.name !== "string") return false
            if (route.path === "/" || route.path === "/en") return true
            return state.pages.some(
                page =>
                    (route.name as string).split("___")[0] === page.to.slice(1)
            )
        },
        getAllPages() {
            const pages: Array<PageItem | DirectoryPageItem> = []
            this.pages.forEach(page => {
                pages.push(page)
                if (page.children) {
                    page.children.forEach(child => {
                        pages.push(child)
                    })
                }
            })
            return pages
        },
    },
    actions: {
        async fetchPages() {
            try {
                const data = await $fetch<DirectoryPageItem[]>(PAGES_URL, {
                    baseURL: import.meta.client
                        ? this.apiBaseUrl
                        : this.apiBaseUrlInt,
                })
                if (data) {
                    this.pages = data.sort((a, b) => a.order - b.order)
                }
                return this.pages
            } catch (error) {
                console.warn("Error fetching pages")
                console.warn(error)
            }
        },
        getPageInfo(pageName: string) {
            return this.getAllPages.find(
                page =>
                    page.to.slice(1).replaceAll("/", "-") ===
                    pageName.split("___")[0]
            )
        },
        getPageDescription(pageName: string) {
            const page = this.getAllPages.find(
                page =>
                    page.to.slice(1).replaceAll("/", "-") ===
                    pageName.split("___")[0]
            )
            const desc = page ? useLocaleValue(page, "description") : ""
            return desc
        },
        getChildrenPages(pageName: string) {
            const page = this.pages.find(page => page.to === pageName)
            return page ? page.children : []
        },
        async fetchKeywords() {
            try {
                const data = await $fetch<KeywordItem[]>("keywords", {
                    baseURL: import.meta.client
                        ? this.apiBaseUrl
                        : this.apiBaseUrlInt,
                })
                this.keywords = data
                return this.keywords
            } catch (error) {
                console.warn("Error fetching keywords")
                console.warn(error)
            }
        },
        async fetchTooltips() {
            try {
                const data = await $fetch<Tooltipy>(TOOLTIPS_URL, {
                    baseURL: import.meta.client
                        ? this.apiBaseUrl
                        : this.apiBaseUrlInt,
                })
                this.tooltips = {
                    indikatory: data.INDIKATORY,
                    jevy: data.JEVY,
                    limity: data.LIMITY,
                    metriky: data.METRIKY,
                    skupinyMetrik: data.SKUPINYMETRIK,
                }
                return this.tooltips
            } catch (error) {
                console.warn("Error fetching tooltips")
                console.warn(error)
                return null
            }
        },
        getTooltip(id: string, type: TooltipType) {
            const item = this.tooltips[type].find(el => {
                return el.KOD.trim() === id.trim()
            })
            if (type === "metriky") {
                const skupina = this.tooltips.skupinyMetrik.find(
                    el =>
                        el.KOD.trim() ===
                        id.trim().split(".").slice(0, -1).join(".")
                )
                if (skupina && item && skupina.NAZEV !== item.NAZEV) {
                    return `${useLocaleValue(skupina, "NAZEV")} - ${useLocaleValue(item, "NAZEV").toLocaleLowerCase()}`
                }
                return item ? useLocaleValue(item, "NAZEV") : ""
            }
            return item ? useLocaleValue(item, "NAZEV") : ""
        },
    },
}) */

if ((import.meta as MetaHot).hot) {
    ;(import.meta as MetaHot).hot?.accept(
        acceptHMRUpdate(useMainStore, (import.meta as MetaHot).hot)
    )
}
